"use client";

import React, { createContext, useState, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const InterviewContext = createContext({
  interviews: [],
});

export const InterviewProvider = ({ children }) => {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getToken = () => localStorage.getItem("token");

  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // ===========================
  // Fetch Interviews
  // ===========================
  const fetchInterviews = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/interviews`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch interviews");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setInterviews(data.data || []);
      } else {
        setError(data.message || "Failed to fetch interviews");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch interviews");
    } finally {
      setLoading(false);
    }
  };

  // ===========================
  // Get Single Interview
  // ===========================
  const getInterview = async (id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/interviews/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to fetch interview",
        };
      }

      return {
        success: true,
        data: data.data,
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to fetch interview",
      };
    }
  };

  // ===========================
  // Schedule Interview
  // ===========================
  const scheduleInterview = async (payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/interviews/schedule`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to schedule interview",
        };
      }

      if (data.status === 201) {
        await fetchInterviews();

        toast.success(data.message);

        return {
          success: true,
          message: data.message,
        };
      }

      return {
        success: false,
        message: data.message,
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to schedule interview",
      };
    }
  };

  // ===========================
  // Update Interview
  // ===========================
  const updateInterview = async (id, payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/interviews/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to update interview",
        };
      }

      if (data.status === 200) {
        await fetchInterviews();

        toast.success(data.message);

        return {
          success: true,
          message: data.message,
        };
      }

      return {
        success: false,
        message: data.message,
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to update interview",
      };
    }
  };

  // ===========================
  // Update Feedback
  // ===========================
  const updateFeedback = async (id, payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/interviews/feedback/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to update feedback",
        };
      }

      if (data.status === 200) {
        await fetchInterviews();

        toast.success(data.message);

        return {
          success: true,
          message: data.message,
        };
      }

      return {
        success: false,
        message: data.message,
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to update feedback",
      };
    }
  };

  // ===========================
  // Update Status
  // ===========================
  const updateInterviewStatus = async (id, payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/interviews/status/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to update status",
        };
      }

      if (data.status === 200) {
        await fetchInterviews();

        toast.success(data.message);

        return {
          success: true,
          message: data.message,
        };
      }

      return {
        success: false,
        message: data.message,
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to update status",
      };
    }
  };

  // ===========================
  // Delete Interview
  // ===========================
  const deleteInterview = async (id, delete_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/interviews/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          delete_audit_id,
        }),
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to delete interview",
        };
      }

      if (data.status === 200) {
        await fetchInterviews();

        toast.success(data.message);

        return {
          success: true,
          message: data.message,
        };
      }

      return {
        success: false,
        message: data.message,
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to delete interview",
      };
    }
  };

  useEffect(() => {
    const token = getToken();

    if (token) {
      fetchInterviews();
    }
  }, []);

  return (
    <InterviewContext.Provider
      value={{
        interviews,
        loading,
        error,
        fetchInterviews,
        getInterview,
        scheduleInterview,
        updateInterview,
        updateFeedback,
        updateInterviewStatus,
        deleteInterview,
        setInterviews,
      }}
    >
      {children}
    </InterviewContext.Provider>
  );
};

export const useInterviews = () => useContext(InterviewContext);